import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import Swal from "sweetalert2";
import axios from "axios";
import {
  MdOutlineClose,
  MdOutlineCheck,
  MdOutlineCrop,
  MdOutlineUpload,
  MdOutlineImage,
} from "react-icons/md";
import moment from "moment-timezone";
import PostModal from "./PostModal";
import BaseUrl from "../Components/BaseUrl";
import Cookies from "js-cookie";

const CreateEventModal = ({ eventmodalOpen, closeModal,onEventCreated }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [eventSpeakers, setEventSpeakers] = useState([]);

  useEffect(() => {
    getSpeakers();
  }, []);

  const getSpeakers = async () => {
    try {
      const token = Cookies.get("token");
      const response = await axios.get(`${BaseUrl}connections`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEventSpeakers(response.data.data);
    } catch (error) {
      console.error("Error fetching speakers:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load speakers. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const filteredSpeakers = eventSpeakers.filter((speaker) =>
    speaker.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const timezones = moment.tz.names();
  const initialEventDetails = {
    event_name: "",
    description: "",
    event_type: "online",
    image: null,
    start_date: format(new Date(), "yyyy-MM-dd"),
    start_time: "10:00",
    timezone: moment.tz.guess(),
    is_endtime: false,
    end_date: format(new Date(), "yyyy-MM-dd"),
    end_time: "18:00",
    venue: "",
    address: "",
    external_event_link: "",
    speakers: [],
  };
  const initialCropArea = {
    x: 0,
    y: 0,
    width: 100,
    height: 56.25,
  };
  const [eventDetails, setEventDetails] = useState(initialEventDetails);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState("");
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [cropArea, setCropArea] = useState(initialCropArea);
  const [originalImage, setOriginalImage] = useState(null);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  // Reset states when modal closes
  useEffect(() => {
    if (!eventmodalOpen) {
      setEventDetails(initialEventDetails);
      setImagePreview(null);
      setSearchTerm("");
      setImageError("");
      setIsEditingImage(false);
      setCropArea(initialCropArea);
      setOriginalImage(null);
      setModalOpen(false);
    }
  }, [eventmodalOpen]);

  useEffect(() => {
    if (eventmodalOpen) {
      setEventDetails((prev) => ({
        ...prev,
        timezone: moment.tz.guess(),
      }));
    }
  }, [eventmodalOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(name, value, type, checked);
    setEventDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectSpeaker = (speaker) => {
    if (!eventDetails.speakers.includes(speaker.id)) {
      setEventDetails((prev) => ({
        ...prev,
        speakers: [...prev.speakers, speaker.id],
      }));
    }
    setSearchTerm("");
  };

  const handleRemoveSpeaker = (id) => {
    setEventDetails((prev) => ({
      ...prev,
      speakers: prev.speakers.filter((speakerId) => speakerId !== id),
    }));
  };

  const areRequiredFieldsFilled = () => {
    const baseFieldsFilled =
      eventDetails.event_name.trim() !== "" &&
      eventDetails.timezone.trim() !== "" &&
      eventDetails.start_date.trim() !== "" &&
      eventDetails.start_time.trim() !== "" &&
      eventDetails.external_event_link.trim() !== "";

    if (eventDetails.event_type === "InPerson") {
      return (
        baseFieldsFilled &&
        eventDetails.address.trim() !== "" &&
        eventDetails.venue.trim() !== ""
      );
    }
    return baseFieldsFilled;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setImageError("File size should not exceed 5MB");
      return;
    }

    setImageError("");

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const minWidth = 480;
        const minHeight = minWidth / (16 / 9);

        if (img.width < minWidth || img.height < minHeight) {
          Swal.fire({
            title: "Image too small",
            text: `For best quality, please use an image with at least ${minWidth}×${minHeight} pixels (16:9 aspect ratio). Your image is ${img.width}×${img.height}.`,
            icon: "error",
            confirmButtonText: "OK",
          });
          return;
        }

        const aspectRatio = img.width / img.height;
        const targetRatio = 16 / 9;

        if (Math.abs(aspectRatio - targetRatio) > 0.01) {
          setOriginalImage(event.target.result);
          setIsEditingImage(true);
          initializeCropArea(img.width, img.height);
        } else {
          setImagePreview(event.target.result);
          setEventDetails((prev) => ({
            ...prev,
            image: file,
          }));
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const initializeCropArea = (imgWidth, imgHeight) => {
    const targetRatio = 16 / 9;
    let cropWidth, cropHeight;

    if (imgWidth / imgHeight > targetRatio) {
      cropHeight = imgHeight;
      cropWidth = cropHeight * targetRatio;
    } else {
      cropWidth = imgWidth;
      cropHeight = cropWidth / targetRatio;
    }

    setCropArea({
      x: (imgWidth - cropWidth) / 2,
      y: (imgHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    });
  };

  const handleCropMove = (e) => {
    const img = imageRef.current;
    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCropArea((prev) => ({
      ...prev,
      x: Math.max(0, Math.min(x - prev.width / 2, img.width - prev.width)),
      y: Math.max(0, Math.min(y - prev.height / 2, img.height - prev.height)),
    }));
  };

  const handleCropResize = (e, direction) => {
    if (!isEditingImage) return;

    e.stopPropagation();
    const img = imageRef.current;
    const rect = img.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = cropArea.width;
    const startHeight = cropArea.height;

    const moveHandler = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;

      let newWidth, newHeight;

      if (direction.includes("e") || direction.includes("w")) {
        newWidth = startWidth + (direction.includes("e") ? dx : -dx);
        newHeight = newWidth / (16 / 9);
      } else {
        newHeight = startHeight + (direction.includes("s") ? dy : -dy);
        newWidth = newHeight * (16 / 9);
      }
      const maxWidth = img.width - cropArea.x;
      const maxHeight = img.height - cropArea.y;

      newWidth = Math.min(newWidth, maxWidth);
      newHeight = Math.min(newHeight, maxHeight);

      setCropArea((prev) => ({
        ...prev,
        width: newWidth,
        height: newHeight,
      }));
    };

    const upHandler = () => {
      document.removeEventListener("mousemove", moveHandler);
      document.removeEventListener("mouseup", upHandler);
    };

    document.addEventListener("mousemove", moveHandler);
    document.addEventListener("mouseup", upHandler);
  };

  const applyCrop = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;
    canvas.width = cropArea.width;
    canvas.height = cropArea.height;
    ctx.drawImage(
      img,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height,
      0,
      0,
      cropArea.width,
      cropArea.height
    );

    const croppedImageUrl = canvas.toDataURL("image/jpeg", 0.9);
    setImagePreview(croppedImageUrl);

    fetch(croppedImageUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "cropped-image.jpg", {
          type: "image/jpeg",
        });
        setEventDetails((prev) => ({
          ...prev,
          image: file,
        }));
      });

    setIsEditingImage(false);
  };

  const cancelCrop = () => {
    setIsEditingImage(false);
    setImagePreview(null);
    setEventDetails((prev) => ({
      ...prev,
      image: null,
    }));
  };

  function getColorFromName(name) {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 50%)`;
  }

  const handlegroupformsubmit = async () => {
    if (!areRequiredFieldsFilled()) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill in all required fields.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    setModalOpen(true);
  };

  const handleEventCreated = () => {
    setEventDetails(initialEventDetails);
    setImagePreview(null);
    setSearchTerm("");
    setImageError("");
    setIsEditingImage(false);
    setCropArea(initialCropArea);
    setOriginalImage(null);
    closeModal();
  };

  return (
    <div>
      {eventmodalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="modal-dialog bg-white rounded-[30px] shadow-lg max-w-xl w-full border-8 border-gray-500">
            <div className="modal-content border-[#F16163]">
              <div className="modal-header bg-white flex justify-between items-center rounded-t-[30px] px-6 py-1 border-b border-gray-200">
                <div className="flex items-start p-2">
                  <p className="text-2xl font-semibold">Create an event</p>
                </div>
                <button
                  type="button"
                  className="text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-full p-2"
                  onClick={closeModal}
                >
                  <MdOutlineClose />
                </button>
              </div>

              <div className="modal-body pb-4 overflow-y-auto max-h-[500px]">
                <div className="space-y-4">
                  <div className="space-y-4">
                    {isEditingImage ? (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="mb-4">
                          <h3 className="text-sm font-medium text-gray-700 mb-2">
                            Adjust the crop area (16:9 aspect ratio)
                          </h3>
                          <div className="relative overflow-hidden rounded-md shadow-sm border-2 border-dashed border-gray-300 bg-gray-100">
                            <img
                              ref={imageRef}
                              src={originalImage}
                              alt="Original"
                              className="block max-w-full max-h-[400px] mx-auto"
                              onMouseDown={handleCropMove}
                            />
                            <div
                              className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20"
                              style={{
                                left: `${cropArea.x}px`,
                                top: `${cropArea.y}px`,
                                width: `${cropArea.width}px`,
                                height: `${cropArea.height}px`,
                                cursor: "move",
                                boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
                              }}
                              onMouseDown={handleCropMove}
                            >
                              {["nw", "ne", "sw", "se"].map((pos) => (
                                <div
                                  key={pos}
                                  className={`absolute w-4 h-4 bg-blue-500 border-2 border-white rounded-full cursor-${pos}-resize hover:bg-blue-600 active:bg-blue-700`}
                                  style={{
                                    [pos.includes("n") ? "top" : "bottom"]:
                                      "-6px",
                                    [pos.includes("w") ? "left" : "right"]:
                                      "-6px",
                                  }}
                                  onMouseDown={(e) => handleCropResize(e, pos)}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            Drag the edges to resize or move the crop area
                          </p>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={cancelCrop}
                            className="flex-1 flex items-center justify-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md transition-colors"
                          >
                            <MdOutlineClose size={18} />
                            Cancel
                          </button>
                          <button
                            onClick={applyCrop}
                            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
                          >
                            <MdOutlineCheck size={18} />
                            Apply Crop
                          </button>
                        </div>
                        <canvas ref={canvasRef} className="hidden" />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-all hover:border-blue-400 hover:bg-blue-50">
                          {imagePreview ? (
                            <div className="relative group">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="mx-auto h-48 w-full object-cover rounded-md shadow-sm"
                                style={{ aspectRatio: "16/9" }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-md transition-all flex items-center justify-center">
                                <button
                                  type="button"
                                  className="opacity-0 group-hover:opacity-100 bg-white p-2 rounded-full shadow-lg transition-opacity hover:bg-gray-100"
                                  onClick={() => {
                                    setImagePreview(null);
                                    setEventDetails((prev) => ({
                                      ...prev,
                                      image: null,
                                    }));
                                  }}
                                  title="Remove image"
                                >
                                  <MdOutlineClose
                                    size={20}
                                    className="text-gray-700"
                                  />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="mx-auto h-12 w-12 text-gray-400">
                                <MdOutlineImage size={48} className="mx-auto" />
                              </div>
                              <div className="flex flex-col sm:flex-row justify-center items-center gap-1 text-sm text-gray-600">
                                <label
                                  htmlFor="image"
                                  className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500"
                                >
                                  <span className="inline-flex items-center gap-1">
                                    <MdOutlineUpload size={16} />
                                    Upload an image
                                  </span>
                                  <input
                                    id="image"
                                    name="image"
                                    type="file"
                                    accept="image/*"
                                    className="sr-only"
                                    onChange={handleImageChange}
                                  />
                                </label>
                                <span className="text-gray-500">
                                  or drag and drop
                                </span>
                              </div>
                              <p className="text-xs text-gray-500">
                                PNG, JPG, GIF (16:9 aspect ratio recommended,
                                max 5MB)
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Type
                    </label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="event_type"
                          value="online"
                          checked={eventDetails.event_type === "online"}
                          onChange={handleChange}
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="ml-2">Online</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="event_type"
                          value="InPerson"
                          checked={eventDetails.event_type === "InPerson"}
                          onChange={handleChange}
                          className="form-radio h-4 w-4 text-blue-600"
                        />
                        <span className="ml-2">In Person</span>
                      </label>
                    </div>
                  </div>

                  <div className="border-b border-gray-200 px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Event Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="event_name"
                      value={eventDetails.event_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 h-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-8"
                      placeholder="Tech Conference 2023"
                    />
                  </div>

                  <div className="px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="timezone"
                      value={eventDetails.timezone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {timezones.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz.replace(/_/g, " ")} (UTC
                          {moment.tz(tz).format("Z")})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="start_date"
                        value={eventDetails.start_date}
                        onChange={handleChange}
                        className="w-full px-3 h-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        name="start_time"
                        value={eventDetails.start_time}
                        onChange={handleChange}
                        className="w-full h-8 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center px-6">
                    <input
                      id="is_endtime"
                      name="is_endtime"
                      type="checkbox"
                      checked={eventDetails.is_endtime}
                      onChange={handleChange}
                      className="h-4 w-4 text-sm text-blue-600 focus:ring-blue-500 rounded"
                    />
                    <label
                      htmlFor="is_endtime"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      This event has an end date/time
                    </label>
                  </div>

                  {eventDetails.is_endtime && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          name="end_date"
                          value={eventDetails.end_date}
                          onChange={handleChange}
                          className="w-full h-8 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Time
                        </label>
                        <input
                          type="time"
                          name="end_time"
                          value={eventDetails.end_time}
                          onChange={handleChange}
                          className="w-full h-8 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  {eventDetails.event_type === "InPerson" && (
                    <>
                      <div className="px-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Venue <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="venue"
                          value={eventDetails.venue}
                          onChange={handleChange}
                          className="w-full h-8 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Convention Center"
                        />
                      </div>
                      <div className="px-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={eventDetails.address}
                          onChange={handleChange}
                          className="w-full h-8 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="123 Main St, City, Country"
                        />
                      </div>
                    </>
                  )}

                  <div className="px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      External event link{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      name="external_event_link"
                      value={eventDetails.external_event_link}
                      onChange={handleChange}
                      className="w-full h-8 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/event"
                    />
                  </div>

                  <div className="px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={eventDetails.description}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Join us for this amazing event..."
                    />
                  </div>

                  <div className="px-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Speaker
                    </label>
                    <input
                      type="text"
                      placeholder="Search speakers"
                      className="w-full p-2 text-sm h-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <div className="w-full mt-1 bg-white border rounded-md shadow-lg">
                        {filteredSpeakers.length > 0 ? (
                          filteredSpeakers.map((speaker) => (
                            <div
                              key={speaker.id}
                              className="flex items-center gap-3 p-2 hover:bg-gray-100"
                              onClick={() => handleSelectSpeaker(speaker)}
                            >
                              {speaker.profile ? (
                                <img
                                  src={speaker.profile}
                                  alt={speaker.name}
                                  className="w-10 h-10 rounded-full"
                                />
                              ) : (
                                <div
                                  className="flex items-center justify-center w-10 h-10 rounded-full text-white font-bold"
                                  style={{
                                    backgroundColor: getColorFromName(
                                      speaker.name
                                    ),
                                  }}
                                >
                                  {speaker.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div>
                                <p className="text-[10px] font-semibold">
                                  {speaker.name}
                                </p>
                                <p className="text-[10px] text-gray-600">
                                  {speaker.headline}
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="p-2 text-gray-500">No results found</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 px-6">
                    {eventDetails.speakers.map((speakerId) => {
                      const speaker = eventSpeakers.find(
                        (s) => s.id === speakerId
                      );
                      if (!speaker) return null;
                      return (
                        <div
                          key={speakerId}
                          className="flex items-center gap-3 p-2 border rounded-md"
                        >
                          {speaker.profile ? (
                            <img
                              src={speaker.profile}
                              alt={speaker.name}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div
                              className="flex items-center justify-center w-10 h-10 rounded-full text-white font-bold"
                              style={{
                                backgroundColor: getColorFromName(speaker.name),
                              }}
                            >
                              {speaker.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-semibold">{speaker.name}</p>
                            <p className="text-sm text-gray-600">
                              {speaker.headline}
                            </p>
                          </div>
                          <button
                            className="p-1 text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveSpeaker(speakerId)}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="border-t">
                <div className="flex justify-end space-x-2 py-4 px-6">
                  <button
                    className={`bg-blue-500 text-white px-4 py-1 rounded-2xl ${
                      !areRequiredFieldsFilled()
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-blue-600"
                    }`}
                    onClick={handlegroupformsubmit}
                    disabled={!areRequiredFieldsFilled()}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {modalOpen && (
         <PostModal
         modalOpen={modalOpen}
         closeModal={() => setModalOpen(false)}
         eventData={eventDetails}
         onEventCreated={(newEvent) => {
           handleEventCreated();
           onEventCreated(newEvent);
         }}
       />
      )}
    </div>
  );
};

export default CreateEventModal;
